import { Box, Button, InputAdornment, Stack, TextField } from '@mui/material';
import { MagnifyingGlassIcon } from '@phosphor-icons/react';

import {
  STANDARD_LAYOUT_PADDING_X,
  STANDARD_LAYOUT_PADDING_Y,
  Text,
  api,
  useLoggedInAuthContext,
} from 'common';

import { type FlowTemplateFilterGroupState } from './Filters/FilterGroup';
import { FlowTemplateTagFilters } from './Filters/TagFilters';
import { FlowTemplateVisibilityFilter } from './Filters/Visibility';
import {
  type UniqueTemplateTagNameByCategory,
  getFacetedTagsByCategoryFromTemplates,
} from './utils';
import { EventAction, EventCategory, useCaptureEvents } from '../../../../utils/events';

interface StoredValues {
  solutionAreaTagNames?: string[];
  useCaseTagNames?: string[];
  flowTypeTagNames?: string[];
  supportedEhrTagNames?: string[];
  visibility?: api.FlowDefinitionTemplate.TemplateVisibility[];
}

interface FilterGroupState {
  state: FlowTemplateFilterGroupState;
  onStateChange: (state: FlowTemplateFilterGroupState) => void;
}

type FilterGroupKey = 'solutionArea' | 'useCase' | 'flowType' | 'supportedEhr' | 'visibility';

interface FlowTemplatePanelProps {
  storedValues: StoredValues;
  updateStoredValue: <K extends keyof StoredValues>(key: K, value: StoredValues[K]) => void;
  setStoredValues: (values: StoredValues) => void;
  searchValue: string;
  setSearchValue: (value: string) => void;
  selectedTagsByCategory: UniqueTemplateTagNameByCategory;
  baseFlowTemplates: api.FlowDefinitionTemplate.FlowDefinitionTemplate[];
  filterGroupStates: Record<FilterGroupKey, FilterGroupState>;
}

export function FlowTemplatePanel(props: FlowTemplatePanelProps) {
  const {
    storedValues,
    updateStoredValue,
    setStoredValues,
    searchValue,
    setSearchValue,
    baseFlowTemplates,
    selectedTagsByCategory,
    filterGroupStates,
  } = props;
  const {
    solutionAreaTagNames,
    useCaseTagNames,
    flowTypeTagNames,
    supportedEhrTagNames,
    visibility,
  } = storedValues;

  const { captureEvent } = useCaptureEvents();
  const { hasPermission } = useLoggedInAuthContext();

  const filterOptions = getFacetedTagsByCategoryFromTemplates({
    templates: baseFlowTemplates,
    selectedTagsByCategory,
  });

  const numberOfSelectedFilters =
    (solutionAreaTagNames?.length ?? 0) +
    (useCaseTagNames?.length ?? 0) +
    (flowTypeTagNames?.length ?? 0) +
    (supportedEhrTagNames?.length ?? 0) +
    (visibility?.length ?? 0);

  return (
    <Box>
      <Box paddingX={STANDARD_LAYOUT_PADDING_X} paddingTop={4} paddingBottom={0}>
        <TextField
          fullWidth
          aria-label="Search"
          placeholder="Search"
          value={searchValue}
          onChange={(event) => {
            setSearchValue(event.target.value);
          }}
          onBlur={() => {
            if (searchValue.length > 2) {
              captureEvent({
                category: EventCategory.FlowDefinitionTemplate,
                action: EventAction.SearchTerm,
                properties: {
                  searchTerm: searchValue,
                },
              });
            }
          }}
          slotProps={{
            input: {
              startAdornment: (
                <InputAdornment position="start">
                  <Box color="text.secondary">
                    <MagnifyingGlassIcon size={24} />
                  </Box>
                </InputAdornment>
              ),
            },
          }}
        />
      </Box>
      <Stack
        paddingX={STANDARD_LAYOUT_PADDING_X}
        paddingY={STANDARD_LAYOUT_PADDING_Y}
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        sx={{ borderBottom: '1px solid', borderColor: 'divider' }}
      >
        <Text variant="heading4" color="text.secondary" bold>
          Filter {numberOfSelectedFilters > 0 ? `(${numberOfSelectedFilters})` : ''}
        </Text>
        {numberOfSelectedFilters > 0 && (
          <Button
            variant="text"
            size="small"
            onClick={() => {
              setStoredValues({});
            }}
          >
            Clear all
          </Button>
        )}
      </Stack>
      <Box>
        <FlowTemplateTagFilters
          tags={filterOptions[api.FlowDefinitionTemplate.TagCategory.SolutionArea]}
          sectionLabel="Solution Area"
          tagCategory={api.FlowDefinitionTemplate.TagCategory.SolutionArea}
          selectedTags={new Set(solutionAreaTagNames ?? [])}
          setSelectedTags={(tags) => updateStoredValue('solutionAreaTagNames', Array.from(tags))}
          sectionState={filterGroupStates.solutionArea.state}
          onSectionStateChange={filterGroupStates.solutionArea.onStateChange}
        />
        <FlowTemplateTagFilters
          tags={filterOptions[api.FlowDefinitionTemplate.TagCategory.UseCase]}
          sectionLabel="Use Case"
          tagCategory={api.FlowDefinitionTemplate.TagCategory.UseCase}
          selectedTags={new Set(useCaseTagNames ?? [])}
          setSelectedTags={(tags) => updateStoredValue('useCaseTagNames', Array.from(tags))}
          sectionState={filterGroupStates.useCase.state}
          onSectionStateChange={filterGroupStates.useCase.onStateChange}
        />
        <FlowTemplateTagFilters
          tags={filterOptions[api.FlowDefinitionTemplate.TagCategory.FlowType]}
          sectionLabel="Flow Type"
          tagCategory={api.FlowDefinitionTemplate.TagCategory.FlowType}
          selectedTags={new Set(flowTypeTagNames ?? [])}
          setSelectedTags={(tags) => updateStoredValue('flowTypeTagNames', Array.from(tags))}
          sectionState={filterGroupStates.flowType.state}
          onSectionStateChange={filterGroupStates.flowType.onStateChange}
        />
        <FlowTemplateTagFilters
          tags={filterOptions[api.FlowDefinitionTemplate.TagCategory.SupportedEhr]}
          sectionLabel="Compatible EHRs"
          tagCategory={api.FlowDefinitionTemplate.TagCategory.SupportedEhr}
          selectedTags={new Set(supportedEhrTagNames ?? [])}
          setSelectedTags={(tags) => updateStoredValue('supportedEhrTagNames', Array.from(tags))}
          sectionState={filterGroupStates.supportedEhr.state}
          onSectionStateChange={filterGroupStates.supportedEhr.onStateChange}
        />
        {(hasPermission.ModifyFlowDefinitionTemplates ||
          hasPermission.PublishFlowDefinitionTemplates) && (
          <FlowTemplateVisibilityFilter
            visibilities={visibility ?? []}
            onChangeVisibility={(visibilities) => {
              setStoredValues({
                visibility: visibilities,
              });
            }}
            sectionState={filterGroupStates.visibility.state}
            onSectionStateChange={filterGroupStates.visibility.onStateChange}
          />
        )}
      </Box>
    </Box>
  );
}
